/**
 * bmu-tools MCP tools: names, input and output schemas, errors and limits
 * (specs/c2-kontratlar.md 6). The tools package derives the MCP inputSchema and
 * outputSchema from these with z.toJSONSchema. Every object is strict; results come
 * back as structuredContent with the same JSON as text in content.
 *
 * No tool ever returns a file path, storage key, internal address, token, API key,
 * e-mail address, phone number or buyer name (6.8).
 */
import { z } from 'zod';
import { configAgentIdSchema, idStringSchema, isoDateTimeSchema } from './ids.js';
/** Tool names; agents.yaml tools lists (packages/shared TOOL_NAMES) must stay equal to this. */
export const TOOL_NAMES = [
    'kb_search',
    'product_card_get',
    'market_vehicle_check',
    'etsy_orders_read',
    'etsy_listings_read',
    'gmail_threads_read',
    'task_create',
    'propose_action',
    'unknown_question_raise',
    'scan_plan_get',
    'query_run_record',
    'lead_candidate_record',
    'catalog_search',
];
export const toolNameSchema = z.enum(TOOL_NAMES);
/** Which database pool serves a tool. The tool decides, never the agent. */
export const TOOL_POOLS = Object.freeze({
    kb_search: 'app',
    product_card_get: 'app',
    market_vehicle_check: 'app',
    etsy_orders_read: 'app',
    etsy_listings_read: 'app',
    gmail_threads_read: 'none',
    task_create: 'app',
    propose_action: 'app',
    unknown_question_raise: 'app',
    scan_plan_get: 'outreach',
    query_run_record: 'outreach',
    lead_candidate_record: 'outreach',
    catalog_search: 'app',
});
export const TOOL_ERROR_CODES = [
    'tool_not_allowed',
    'invalid_input',
    'not_found',
    'feature_disabled',
    'tool_unavailable',
    'policy_violation',
    'duplicate_pending',
    'out_of_plan',
    'kill_switch',
    'rate_limited',
    'internal',
];
export const toolErrorCodeSchema = z.enum(TOOL_ERROR_CODES);
/** structuredContent of an error result (isError: true). */
export const toolErrorSchema = z.object({
    error: z.object({
        code: toolErrorCodeSchema,
        message_tr: z.string().min(1).max(500),
        /** duplicate_pending carries the existing approval. */
        approval_id: idStringSchema.optional(),
    }),
});
/** Limits per run (6.2). */
export const TOOL_LIMITS = Object.freeze({
    callsPerRun: 60,
    proposeActionPerRun: 5,
    taskCreatePerRun: 10,
    /** Tool result summary kept in run_steps.detail. */
    resultSummaryChars: 2000,
});
/** Tool session tokens: "bmt_" + base64url of 32 random bytes, at most 45 minutes. */
export const TOOL_TOKEN_PREFIX = 'bmt_';
export const TOOL_TOKEN_PATTERN = /^bmt_[A-Za-z0-9_-]{43}$/;
export const TOOL_TOKEN_MAX_TTL_MS = 45 * 60 * 1000;
/** Streamable HTTP MCP endpoint, loopback only. */
export const TOOLS_MCP_PATH = '/mcp';
export const TOOLS_DEFAULT_PORT = 7801;
export const TOOLS_HOST = '127.0.0.1';
/** twin.tasks.kind. */
export const TASK_KINDS = [
    'customer_reply',
    'delivery_message',
    'listing_draft',
    'listing_update',
    'kb_draft',
    'kb_v0',
    'content',
    'site_pr',
    'social_draft',
    'report',
    'plan',
    'review',
    'lead_research',
    'outreach_draft',
    'reply_triage',
    'media_render',
    'media_gen',
    'ingest',
    'request_followup',
    'custom_request',
    'other',
];
export const taskKindSchema = z.enum(TASK_KINDS);
const subjectSchema = z.strictObject({
    type: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/),
    ref: z.string().min(1).max(200),
});
const countryCode = z.string().regex(/^[A-Z]{2}$/);
// kb_search ----------------------------------------------------------------
export const kbSearchInputSchema = z.strictObject({
    query: z.string().min(2).max(200),
    lang: z.enum(['en', 'tr']).optional(),
    product_id: idStringSchema.optional(),
    limit: z.number().int().min(1).max(10).optional(),
});
export const kbSearchOutputSchema = z.strictObject({
    items: z.array(z.strictObject({
        kb_entry_id: idStringSchema,
        question_en: z.string(),
        answer_en: z.string(),
        answer_tr: z.string().nullable(),
        product_ids: z.array(idStringSchema),
        video_ids: z.array(idStringSchema),
        updated_at: isoDateTimeSchema,
    })),
    total: z.number().int().nonnegative(),
});
// product_card_get -----------------------------------------------------------
/*
 * ETSY_TEXT_RULE (v0.1.173). Sercan, 2 Oct 2026: no text that came from the Etsy API goes to an external model until
 * Etsy gives written permission (the Etsy API Terms of 18 Aug 2026 forbid using API content for AI without written
 * authorization), and every model Twin uses is external. Every output of these shop tools reaches a model (the GM over
 * MCP, the Genesis roles through the worker), so product_card_get and etsy_listings_read carry no listing title,
 * description or tag: only what we set ourselves (ids, state, price, link) and our own catalog. The schemas are strict,
 * so a query that selected the text again would fail here. The text stays in shop.listings and on the panel.
 */
export const productCardGetInputSchema = z
    .strictObject({
    product_id: idStringSchema.optional(),
    sku: z.string().min(1).max(64).optional(),
    listing_id: z
        .string()
        .regex(/^\d{1,20}$/)
        .optional(),
})
    .refine((input) => [input.product_id, input.sku, input.listing_id].filter((v) => v !== undefined).length === 1, {
    error: 'give exactly one of product_id, sku, listing_id',
});
export const productCardGetOutputSchema = z.strictObject({
    product_id: idStringSchema,
    sku: z.string().nullable(),
    title_en: z.string(),
    status: z.string(),
    /** v0.1.26: the catalog fields the Temsilci may cite. Null means the card does not know. */
    product_type: z.string(),
    coverage: z.string(),
    poly_count: z.number().int().nonnegative().nullable(),
    resolution_mm: z.number().nullable(),
    file_size_mb: z.number().nullable(),
    cab_types: z.array(z.string()),
    trims: z.array(z.string()),
    includes_frame: z.boolean().nullable(),
    delivery_method: z.string(),
    vehicle: z
        .strictObject({
        vehicle_id: idStringSchema,
        make: z.string(),
        model: z.string(),
        generation: z.string().nullable(),
        year_from: z.number().int().nullable(),
        year_to: z.number().int().nullable(),
    })
        .nullable(),
    accuracy_mm: z.number().nullable(),
    compatible_year_from: z.number().int().nullable(),
    compatible_year_to: z.number().int().nullable(),
    file_formats: z.array(z.string()),
    package_contents: z.array(z.string()),
    price_cents: z.number().int().nullable(),
    geometry_cards: z.array(z.strictObject({ geometry_card_id: idStringSchema, asset_id: z.uuid(), kind: z.string() })),
    /** v0.1.63: renders and screenshots of the product's own files (twin.assets with product_id), renders first, at most six. */
    images: z.array(z.strictObject({
        asset_id: z.uuid(),
        kind: z.enum(['render', 'preview', 'photo']),
        title: z.string().nullable(),
        mime_type: z.string(),
    })),
    listing_ids: z.array(z.string()),
    /**
     * v0.1.47: the product's Etsy listings with their links, the only links a customer reply may carry.
     * v0.1.173: id, state, price and link only (ETSY_TEXT_RULE above); the title and the description (v0.1.72) left.
     */
    listings: z.array(z.strictObject({
        listing_id: z.string(),
        state: z.string(),
        price_usd: z.number().nullable(),
        url: z.string(),
    })),
    field_provenance: z.record(z.string(), z.enum(['file', 'sercan'])),
});
// catalog_search ---------------------------------------------------------------
/*
 * v0.1.235: what the shop sells for a vehicle. On-sale products only (an active Etsy listing), matched from the
 * vehicle words of a text (model, the shop's aliases, market names such as Triton or Revo, make, generation, years).
 * ETSY_TEXT_RULE applies: our own catalog title and notes, the listing's id, price and link, no listing text.
 */
export const catalogSearchInputSchema = z.strictObject({
    /** Vehicle words or a whole customer text: matched in code, never sent anywhere. */
    query: z.string().min(2).max(4000),
    year: z.number().int().min(1950).max(2100).optional(),
    limit: z.number().int().min(1).max(10).optional(),
});
export const catalogSearchOutputSchema = z.strictObject({
    items: z.array(z.strictObject({
        product_id: idStringSchema,
        sku: z.string(),
        title_en: z.string(),
        /** scan, model (an STL made from CAD or model data: never call it a scan), step, plan, bundle... */
        product_type: z.string(),
        coverage: z.string(),
        vehicle: z.strictObject({ make: z.string(), model: z.string(), generation: z.string().nullable() }),
        compatible_year_from: z.number().int().nullable(),
        compatible_year_to: z.number().int().nullable(),
        /** The text asked a year and this product's years are not known. */
        years_unknown: z.boolean(),
        file_formats: z.array(z.string()),
        file_size_mb: z.number().nullable(),
        /** What the files cover, from our catalog (English). */
        covers: z.string().nullable(),
        files: z.array(z.strictObject({ name: z.string(), note: z.string().nullable() })),
        price_usd: z.number().nullable(),
        listing_id: z.string(),
        url: z.string(),
        matched_on: z.array(z.string()),
    })),
    total: z.number().int().nonnegative(),
    years: z.array(z.number().int()),
});
// market_vehicle_check -------------------------------------------------------
export const marketVehicleCheckInputSchema = z.strictObject({
    vehicle_id: idStringSchema,
    country_code: countryCode,
});
export const marketVehicleCheckOutputSchema = z.strictObject({
    vehicle_id: idStringSchema,
    country_code: countryCode,
    officially_sold: z.boolean().nullable(),
    our_orders: z.number().int().nonnegative(),
    source_ref: z.string().nullable(),
    checked_at: isoDateTimeSchema.nullable(),
});
// etsy_orders_read -------------------------------------------------------------
export const etsyOrdersReadInputSchema = z.strictObject({
    since: isoDateTimeSchema.optional(),
    until: isoDateTimeSchema.optional(),
    limit: z.number().int().min(1).max(100).optional(),
    cursor: z.string().min(1).max(200).optional(),
});
export const etsyOrdersReadOutputSchema = z.strictObject({
    items: z.array(z.strictObject({
        order_id: idStringSchema,
        ordered_at: isoDateTimeSchema,
        country_code: countryCode.nullable(),
        total_cents: z.number().int(),
        status: z.string(),
        items: z.array(z.strictObject({
            product_id: idStringSchema.nullable(),
            listing_id: z.string().nullable(),
            quantity: z.number().int().positive(),
            price_cents: z.number().int(),
        })),
    })),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative(),
});
// etsy_listings_read -----------------------------------------------------------
export const etsyListingsReadInputSchema = z.strictObject({
    listing_id: z
        .string()
        .regex(/^\d{1,20}$/)
        .optional(),
    product_id: idStringSchema.optional(),
    limit: z.number().int().min(1).max(50).optional(),
    cursor: z.string().min(1).max(200).optional(),
});
export const etsyListingsReadOutputSchema = z.strictObject({
    items: z.array(z.strictObject({
        listing_id: z.string(),
        product_id: idStringSchema.nullable(),
        // v0.1.173: no title and no tags (ETSY_TEXT_RULE at product_card_get).
        price_cents: z.number().int(),
        state: z.string(),
        views_30d: z.number().int().nonnegative().nullable(),
        orders_30d: z.number().int().nonnegative().nullable(),
        updated_at: isoDateTimeSchema,
    })),
    next_cursor: z.string().nullable(),
});
// gmail_threads_read (always tool_unavailable in C2) -----------------------------
export const gmailThreadsReadInputSchema = z.strictObject({
    query: z.string().min(1).max(200).optional(),
    limit: z.number().int().min(1).max(20).optional(),
});
export const gmailThreadsReadOutputSchema = z.strictObject({ items: z.array(z.strictObject({})) });
// task_create --------------------------------------------------------------------
export const taskCreateInputSchema = z.strictObject({
    title: z.string().min(3).max(300),
    kind: taskKindSchema,
    done_definition: z.string().min(5).max(2000),
    assigned_agent_id: configAgentIdSchema.optional(),
    priority: z.number().int().min(1).max(5).optional(),
    due_at: isoDateTimeSchema.optional(),
    /** Defaults to the task of the token; otherwise that task or one opened in this run. */
    parent_task_id: idStringSchema.optional(),
    subject: subjectSchema.optional(),
});
export const taskCreateOutputSchema = z.strictObject({
    task_id: idStringSchema,
    status: z.enum(['open', 'assigned']),
    created: z.boolean(),
});
// propose_action (6.5) -------------------------------------------------------------
export const APPROVAL_SOURCE_KINDS = [
    'kb_entry',
    'product',
    'geometry_card',
    'listing',
    'evidence',
    'order',
    'conversation',
    'playbook',
    'url',
    'metric',
];
/** The task comes from the token (ToolContext.taskId), never from the agent. */
export const proposeActionInputSchema = z.strictObject({
    action_type: z.string().regex(/^[a-z][a-z0-9_]{2,47}$/),
    title_tr: z.string().min(3).max(200),
    reason_tr: z.string().min(1).max(2000),
    /** Validated against ACTION_PAYLOAD_SCHEMAS[action_type]. */
    payload: z.record(z.string(), z.unknown()),
    subject: subjectSchema.optional(),
    customer_waiting: z.boolean().optional(),
    confidence: z.enum(['high', 'low']).optional(),
    sources: z
        .array(z.strictObject({
        kind: z.enum(APPROVAL_SOURCE_KINDS),
        ref: z.string().min(1).max(500),
        label_tr: z.string().min(1).max(200).optional(),
    }))
        .max(20)
        .optional(),
    preview: z
        .strictObject({
        summary_tr: z.string().min(1).max(2000),
        rationale_tr: z.string().max(2000).optional(),
        impact_tr: z.string().max(1000).optional(),
    })
        .optional(),
});
export const proposeActionOutputSchema = z.strictObject({
    approval_id: idStringSchema,
    status: z.literal('pending'),
    created: z.boolean(),
    verifier: z.literal('queued'),
    expires_at: isoDateTimeSchema,
});
// unknown_question_raise -------------------------------------------------------------
export const unknownQuestionRaiseInputSchema = z.strictObject({
    question_en: z.string().min(5).max(500),
    question_tr: z.string().min(1).max(500).optional(),
    /** v0.1.42: why the agent asks (Turkish), the customer's own words and up to four answer choices. */
    why_tr: z.string().min(1).max(600).optional(),
    customer_quote_en: z.string().min(1).max(600).optional(),
    options_tr: z.array(z.string().min(1).max(120)).max(4).optional(),
    conversation_id: idStringSchema.optional(),
    product_id: idStringSchema.optional(),
    customer_waiting: z.boolean(),
});
export const unknownQuestionRaiseOutputSchema = z.strictObject({
    unknown_question_id: idStringSchema,
    request_id: idStringSchema.nullable(),
    deduplicated: z.boolean(),
});
// Regional rep tools (lead engine, off behind feature.lead_engine) (6.6) -------------
/**
 * E-mail addresses and phone numbers. The e-mail rule is the Genesis output rule
 * (packages/agents noContactValues). Phone numbers are an international number
 * (+ and at least 8 more digits) or grouped digits such as "0532 123 45 67" or
 * "(555) 123-4567"; bare digit runs (epoch milliseconds, ids) and dates are not phones.
 */
export const CONTACT_VALUE_PATTERNS = [
    /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){0,8}\.[A-Za-z]{2,24}/,
    /(?<![\w+])\+\d[\d\s().-]{7,}\d(?!\w)/,
    /(?<![\w.:-])\(?\d{3,4}\)?[\s.-]\d{3,4}[\s.-]\d{2,4}(?:[\s.-]\d{2,4})?(?![\w.:-])/,
];
export function containsContactValue(text) {
    return CONTACT_VALUE_PATTERNS.some((pattern) => pattern.test(text));
}
const noContact = (max) => z
    .string()
    .max(max)
    .refine((value) => !containsContactValue(value), { error: 'no e-mail address or phone number' });
const httpUrl = z
    .string()
    .max(2000)
    .regex(/^https?:\/\/\S+$/i, { error: 'expected an http(s) URL' });
export const scanPlanGetInputSchema = z.strictObject({
    scan_run_id: idStringSchema,
    cell_id: z.string().min(1).max(40),
});
export const scanPlanGetOutputSchema = z.strictObject({
    scan_run_id: idStringSchema,
    cell_id: z.string(),
    cell: z.strictObject({
        h3: z.string(),
        status: z.string(),
        bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    }),
    keywords: z.array(z.strictObject({ query_id: idStringSchema, text: z.string(), source: z.string() })),
    sources_allowed: z.array(z.string()),
    max_queries: z.number().int().nonnegative(),
    blocked_domains: z.array(z.string()),
});
export const queryRunRecordInputSchema = z.strictObject({
    scan_run_id: idStringSchema,
    cell_id: z.string().min(1).max(40),
    query: noContact(500).pipe(z.string().min(1)),
    source: z.string().min(1).max(64),
    result_count: z.number().int().nonnegative(),
    results: z.array(z.strictObject({ url: httpUrl, title: noContact(500), rank: z.number().int().positive() })).max(50),
});
export const queryRunRecordOutputSchema = z.strictObject({
    query_run_id: idStringSchema,
    query_result_ids: z.array(idStringSchema),
});
export const LEAD_CLAIM_TYPES = ['role', 'vehicle', 'product', 'channel_type', 'location', 'activity'];
export const leadCandidateRecordInputSchema = z.strictObject({
    query_run_id: idStringSchema,
    query_result_id: idStringSchema.optional(),
    company_name: noContact(300).pipe(z.string().min(1)),
    website: httpUrl.nullable(),
    /** From address evidence, never from the domain ending. */
    country: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    city: noContact(100).nullable(),
    found_by: z.strictObject({ query: noContact(500), source: z.string().min(1).max(64), url: httpUrl }),
    claims: z
        .array(z.strictObject({
        type: z.enum(LEAD_CLAIM_TYPES),
        value: noContact(500),
        source_url: httpUrl,
        quote: noContact(1000),
    }))
        .max(20),
    channel_type: z.enum(['role_email', 'contact_form', 'business_phone', 'none_seen']),
    playbook_segment: z.string().min(1).max(64),
});
export const leadCandidateRecordOutputSchema = z.strictObject({
    /** The query_result_id. */
    candidate_id: idStringSchema,
    dedupe: z.enum(['new', 'duplicate']),
    duplicate_of: idStringSchema.nullable(),
    stage_item: z.literal('pending'),
});
/** Input and output schema of every tool. */
export const TOOL_SCHEMAS = Object.freeze({
    kb_search: { input: kbSearchInputSchema, output: kbSearchOutputSchema },
    product_card_get: { input: productCardGetInputSchema, output: productCardGetOutputSchema },
    market_vehicle_check: { input: marketVehicleCheckInputSchema, output: marketVehicleCheckOutputSchema },
    etsy_orders_read: { input: etsyOrdersReadInputSchema, output: etsyOrdersReadOutputSchema },
    etsy_listings_read: { input: etsyListingsReadInputSchema, output: etsyListingsReadOutputSchema },
    gmail_threads_read: { input: gmailThreadsReadInputSchema, output: gmailThreadsReadOutputSchema },
    task_create: { input: taskCreateInputSchema, output: taskCreateOutputSchema },
    propose_action: { input: proposeActionInputSchema, output: proposeActionOutputSchema },
    unknown_question_raise: { input: unknownQuestionRaiseInputSchema, output: unknownQuestionRaiseOutputSchema },
    scan_plan_get: { input: scanPlanGetInputSchema, output: scanPlanGetOutputSchema },
    query_run_record: { input: queryRunRecordInputSchema, output: queryRunRecordOutputSchema },
    lead_candidate_record: { input: leadCandidateRecordInputSchema, output: leadCandidateRecordOutputSchema },
    catalog_search: { input: catalogSearchInputSchema, output: catalogSearchOutputSchema },
});
/**
 * Patterns no tool output may contain (6.8, integration scenario S14). The integration
 * suite scans every tool output, API response and NOTIFY payload with these.
 */
export const LEAK_PATTERNS = [
    /\bpii\//,
    /\/var\/lib/,
    /\bvault\b/,
    /127\.0\.0\.1/,
    /\bBearer\b/,
    /\bbmt_[A-Za-z0-9_-]{8,}/,
    /storageKey/,
    ...CONTACT_VALUE_PATTERNS,
];
//# sourceMappingURL=tools.js.map